import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { authorityText, formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Input, SourceText, Textarea, useSave } from "@ncp/ui";
import { useStaffName } from "@/lib/staff-names";
import { SectionTitle } from "./components";
import type { OpinionSending, ReviewDetail } from "./api";

/**
 * The adopted opinion's sendings to the authority that issues the instrument: to whom, on which day and under which covering letter, each recorded by
 * an authorised officer under the delegation-of-authority matrix. The overview counts as sent only the opinions whose sending is recorded here.
 */
export function OpinionSendings({ review }: { review: ReviewDetail }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const staffName = useStaffName();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const blank = { recipient: review.issuingAuthority, sentOn: today, reference: "", note: "" };
  const [form, setForm] = useState(blank);
  const record = useSave({
    mutationFn: () => api.post<OpinionSending>(`/api/advocacy/reviews/${review.id}/sendings`, { recipient: form.recipient.trim() || null, sentOn: form.sentOn || null, reference: form.reference.trim(), note: form.note.trim() || null }),
    expect: "record",
    saved: (s) => t("backoffice:processes.advocacy.sent", { recipient: authorityText(s.recipient, locale) }),
    onSuccess: () => { setOpen(false); setForm(blank); void qc.invalidateQueries({ queryKey: ["advocacy"] }); },
  });
  const sendings = review.sendings ?? [];
  return (
    <Card className="p-5" data-section="opinion-sendings">
      <SectionTitle hint={t("backoffice:processes.advocacy.sendingsHint")}>{t("backoffice:processes.advocacy.sendings")}</SectionTitle>
      {sendings.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:processes.advocacy.notSent")}</p> : (
        <ul className="space-y-2 text-sm">
          {sendings.map((s, i) => (
            <li key={i} className="rounded-lg border border-sand-200 p-2.5">
              <p className="font-medium">{authorityText(s.recipient, locale)} · {formatDate(s.sentOn, locale)}</p>
              <p className="text-xs text-muted-foreground"><SourceText text={s.reference} /> · {t("backoffice:processes.advocacy.recordedBy", { name: staffName(s.recordedByName) })}</p>
              {s.note ? <p className="mt-1 text-xs" dir="auto"><SourceText text={s.note} /></p> : null}
            </li>
          ))}
        </ul>
      )}
      {review.maySend ? <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setOpen(true)}><Send /> {t("backoffice:processes.advocacy.recordSending")}</Button> : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md">
          <DialogHeader><DialogTitle>{t("backoffice:processes.advocacy.recordSending")}</DialogTitle><DialogDescription>{t("backoffice:processes.advocacy.recordHint")}</DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label={t("backoffice:processes.advocacy.recipient")} required className="md:col-span-2">{(id) => <Input id={id} value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} dir="auto" />}</Field>
            <Field label={t("backoffice:processes.advocacy.sentOn")} required>{(id) => <Input id={id} type="date" max={today} value={form.sentOn} onChange={(e) => setForm({ ...form, sentOn: e.target.value })} />}</Field>
            <Field label={t("backoffice:processes.advocacy.reference")} required hint={t("backoffice:processes.advocacy.referenceHint")}>{(id) => <Input id={id} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} dir="auto" />}</Field>
          </div>
          <Field label={t("backoffice:processes.advocacy.note")}>{(id) => <Textarea id={id} rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} draft={`advocacy:${review.id}:sending:note`} dir="auto" />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={form.reference.trim().length < 4 || !form.sentOn} loading={record.isPending} onClick={() => record.mutate()}><Send /> {t("backoffice:processes.advocacy.record")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
